import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { AudioPlayer } from "./audio-player/audio-player";
import { isSameDate } from "../../../helpers/isSameDate";
import images from "../../../assets/images";
import {
  getMessageMenu,
  sendMessageMenuPress,
  sendReaction,
} from "../../../axios/chat";
import {
  ContentBlock,
  Message,
  MessageMenuItem,
  CONTENT_BLOCK_TYPES_BLOCKED_FOR_TTS,
  MESSAGE_TYPES_BLOCKED_FOR_TTS,
  TAB_IDS_BLOCKED_FOR_TTS,
} from "../../../types";
import { MessageMenuDropdown } from "../message-menu-dropdown/message-menu-dropdown";
import { useAppStore } from "../../../stores/appStore";
import { useChatStore } from "../../../stores/chatStore";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../utils/internetConnection";
import { TextMessage } from "./text-message/text-message";
import { DateBadge } from "./date-badge/date-badge";
import MessageButtons from "./message-buttons/message-buttons";
import { ImageMessage } from "./image-messages/image-message/image-message";
import { FeedbackInput } from "./feedback-input/feedback-input";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MenuEmojiIcon,
} from "../../../assets/icon";
import { AvatarWithCircle } from "../../../components/user-menu-trigger/user-avatar/avatar-with-circle";
import CalendarWeek from "./calendar-week/calendar-week";
import { IndependentTtsButton } from "./independent-tts/independent-tts-button";
import { motion } from "framer-motion";
import { MessageCharts } from "./message-charts/message-charts";
import { FlashCardMessage } from "./flash-card-message/flash-card-message";
import { FlippingCardMessage } from "./flipping-card-message/flipping-card-message";
import {
  ActiveTabInfo,
  getNormalizedTabbedTabs,
  getTabType,
  TabbedMessage,
} from "./tabbed-message/tabbed-message";
import { getTabTtsSpeakableText } from "./tabbed-message/content-block-renderer";
import { ImageCollection } from "./image-messages/image-collection/image-collection";
import { CrashCourseForm } from "./crash-course-form/crash-course-form";
import { submitCrashCourseForm } from "../../../axios/chat";
import { FlashImageCollection } from "./image-messages/flash-image-collection/flash-image-collection";
import { AuthenticatedFileLink } from "../../authenticated-media/authenticated-file-link";
import { VideoPlayer } from "./video-player/video-player";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import { useMessageInteractionTracking } from "../../../hooks/useMessageInteractionTracking";
import { getLessonTitleFromAny } from "../../../helpers/messageHelpers";
import { messageMenuCache } from "../../../helpers/messageMenuCache";
import {
  getVisualizationMessageUi,
  hasRenderableVisualization,
  resolveMessageMenuHint,
} from "../../../helpers/visualizationMessage";
import { VisualizationMessageContent } from "./visualization-message-content/visualization-message-content";
import { resolveTranscriptData } from "../../../utils/transcription";
import { TabbedMessageStarRating } from "./tabbed-message-star-rating/tabbed-message-star-rating";

interface MessageBubbleProps {
  message: Message;
  isBot: boolean;
  previousMessage?: Message;
  nextMessage?: Message;
  headerVisible: boolean;
  prevMessageHeight: number;
  scrollToPreviousUserMessage: (message_id: string) => void;
  scrollToNextUserMessage: (message_id: string) => void;
  messageEl?: HTMLDivElement | null;
  setMessageRef?: (el: HTMLDivElement | null) => void;
  scrollToPreviousLessonMessage: (message_id: string) => void;
  scrollToNextLessonMessage: (message_id: string) => void;
  isStatic?: boolean;
  isOneMessage?: boolean;
  hideReactionsButton?: boolean;
  // Optional: used by onboarding previews; main chat no longer renders the linked-message line.
  bubbleIndex?: number;
}

// Vertical connector line (removed). Kept commented for reference.
// const GRADIENT_NORMAL = "linear-gradient(180deg, #28C9DC 0%, #8957CF 100%)";
// const GRADIENT_FLIPPED = "linear-gradient(0deg, #28C9DC 0%, #8957CF 100%)";

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isBot,
  previousMessage,
  nextMessage,
  scrollToNextUserMessage,
  scrollToPreviousUserMessage,
  messageEl,
  setMessageRef,
  scrollToPreviousLessonMessage,
  scrollToNextLessonMessage,
  headerVisible,
  prevMessageHeight,
  isStatic,
  isOneMessage,
  hideReactionsButton,
}) => {
  const {
    markedIds,
    botAvatar,
    userAvatar,
    isBrowserLocked,
    messageTheme,
    focus: appFocus,
    domain: appDomain,
    isOnline,
  } = useAppStore();
  const messageRef = useRef<HTMLDivElement | null>(null);
  const { trackInteraction } = useMessageInteractionTracking(
    message,
    messageRef,
  );
  const {
    setMessages,
    updateFavoriteStatus,
    cacheTabbedMessageLabels,
    cacheLessonTitle,
    updateTagStatus,
    fetchTagMessages,
    setCopyMessage,
  } = useChatStore();
  const theme = useTheme();

  // useMessageInteractionTracking(
  //   isBot && message.message_type !== "system" ? message.message_id : ""
  // );

  const [, setLargeHigth] = useState(true);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [currentReaction, setCurrentReaction] = useState<string | undefined>(
    message.reaction,
  );
  const [isSaved, setIsSaved] = useState<boolean | undefined>(message.isSaved);

  const [selectedTextState, setSelectedTextState] = useState<string>("");
  const [tags, setTags] = useState<string[] | undefined>(message.tag!);
  const [flashCardText, setFlashCardText] = useState<string>("");
  const [flippingCardText, setFlippingCardText] = useState<string>("");
  const [tabbedMessageText, setTabbedMessageText] = useState<string>("");
  const [tabbedFlashCardText, setTabbedFlashCardText] = useState<string>("");
  const [tabbedFlippingCardText, setTabbedFlippingCardText] =
    useState<string>("");
  const [activeTabInfo, setActiveTabInfo] = useState<ActiveTabInfo | null>(
    null,
  );

  const tabbedTtsSpeakableText = useMemo(() => {
    if (message.type !== "tabbed_message" || !activeTabInfo?.tabId) return "";
    const tabs = getNormalizedTabbedTabs(message);
    const tab = tabs.find((t) => t.tab_id === activeTabInfo.tabId);
    return tab ? getTabTtsSpeakableText(tab) : "";
  }, [message, activeTabInfo?.tabId]);

  /** Lead block type of the open tab from normalized payload (same source as `TabbedMessage`). */
  const openTabLeadBlockType = useMemo((): string | null => {
    if (message.type !== "tabbed_message" || !activeTabInfo?.tabId) return null;
    const tab = getNormalizedTabbedTabs(message).find(
      (t) => t.tab_id === activeTabInfo.tabId,
    );
    return tab ? getTabType(tab) : null;
  }, [message, activeTabInfo?.tabId]);

  const ttsDisabledForCurrentTabbedTab = useMemo(() => {
    if (message.type !== "tabbed_message" || !activeTabInfo?.tabId) {
      return false;
    }
    if (TAB_IDS_BLOCKED_FOR_TTS.has(activeTabInfo.tabId)) return true;
    const lead = openTabLeadBlockType;
    return Boolean(
      lead &&
        CONTENT_BLOCK_TYPES_BLOCKED_FOR_TTS.has(lead as ContentBlock["type"]),
    );
  }, [message.type, activeTabInfo?.tabId, openTabLeadBlockType]);

  const ttsAllowedForMessageType = !MESSAGE_TYPES_BLOCKED_FOR_TTS.has(
    message.type,
  );
  const menuIconShakeAnimation = {
    rotate: [0, -8, 8, -6, 6, -3, 3, 0],
  };
  const menuIconShakeTransition = {
    duration: 0.9,
    ease: "easeInOut" as const,
    repeat: Infinity,
    repeatDelay: 2.2,
  };
  const [contextMenuItems, setContextMenuItems] = useState<
    MessageMenuItem[] | undefined
  >(undefined);
  const [contextMenuLoading, setContextMenuLoading] = useState(false);
  const isVisualizeOutputMessage = useMemo(() => {
    if (message.type === "visualization_message") return true;

    const title = (message.title || "").toLowerCase();
    const text = (message.text || "").toLowerCase();
    const description = (message.description || "").toLowerCase();

    return (
      title.includes("visualize it") ||
      text.includes("visualize it") ||
      description.includes("visualize it")
    );
  }, [message]);
  const visualizationMenuText = useMemo(() => {
    if (message.type !== "visualization_message") return "";
    return getVisualizationMessageUi(message).text;
  }, [message]);
  const defaultMenuSourceText = useMemo(() => {
    return (
      message.text?.trim() ||
      message.description?.trim() ||
      message.title?.trim() ||
      message.reply?.trim() ||
      message.reply_message_text?.trim() ||
      ""
    );
  }, [message]);
  const resolvedTranscript = resolveTranscriptData({
    transcription: message.transcription ?? message.audio?.transcription,
    transcriptionTimed:
      message.transcription_timed ?? message.audio?.transcription_timed,
  });

  const handleFeedbackSubmitted = async () => {
    try {
      const currentTags = tags || [];
      if (!currentTags.includes("SENT_FEEDBACK")) {
        const newTags = [...currentTags, "SENT_FEEDBACK"];
        await updateTagStatus(message.message_id, newTags);
        setTags(newTags);
        await fetchTagMessages();
      }
    } catch (error) {
      console.error("Error adding SENT_FEEDBACK tag:", error);
    }
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();

      if (selection && selection.anchorNode && text && text.length > 0) {
        if (messageEl && messageEl.contains(selection.anchorNode)) {
          setSelectedTextState((prev) => (prev !== text ? text : prev));
        } else {
          setSelectedTextState((prev) => (prev !== "" ? "" : prev));
        }
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [messageEl]);

  useEffect(() => {
    setCurrentReaction(message.reaction);
  }, [message.reaction]);

  useEffect(() => {
    setIsSaved(message.isSaved);
  }, [message.isSaved]);

  useEffect(() => {
    const cleanupOldFeedback = () => {
      const lastCleanup = sessionStorage.getItem("feedback_cleanup_done");
      if (lastCleanup) return;

      const keys = Object.keys(localStorage);
      const feedbackKeys = keys.filter((key) =>
        key.startsWith("feedback_sent_"),
      );

      if (feedbackKeys.length > 100) {
        const oldKeys = feedbackKeys.slice(0, feedbackKeys.length - 100);
        oldKeys.forEach((key) => localStorage.removeItem(key));
      }

      sessionStorage.setItem("feedback_cleanup_done", "true");
    };

    const timeoutId = setTimeout(cleanupOldFeedback, 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const t = event.target;
      if (t instanceof Element) {
        if (
          t.closest(".custom-menu-paper") ||
          t.closest(".message-menu-subpopover")
        ) {
          return;
        }
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (message.type === "tabbed_message" && activeTabInfo) {
      setContextMenuItems(undefined);
    }
  }, [message.type, activeTabInfo]);

  useEffect(() => {
    setContextMenuItems(undefined);
  }, [appFocus, appDomain]);

  const fetchContextMenuItems = useCallback(
    (position?: { top: number; left: number }) => {
      if (contextMenuLoading) return;

      const openMenu = () => {
        if (position) setMenuPosition(position);
      };

      if (contextMenuItems !== undefined) {
        openMenu();
        return;
      }

    const tab =
      message.type === "tabbed_message"
        ? activeTabInfo
          ? message.tabbed_content?.find(
              (t) => t.tab_id === activeTabInfo.tabId,
            )
          : message.tabbed_content?.[0]
        : null;
    const blocks = Array.isArray(tab?.content_blocks)
      ? (tab.content_blocks as { type?: string }[])
      : message.type === "tabbed_message"
        ? []
        : null;

    const getContentType = (): string => {
      if (message.type === "visualization_message") {
        return "visualization_message";
      }
      if (message.type !== "tabbed_message") return message.type;
      const first = blocks?.[0]?.type;
      return first ?? activeTabInfo?.tabType ?? "text";
    };

    const contentType = getContentType();
    const messageType = message.message_type ?? "default";
    const tabId = tab?.tab_id ?? "";
    const tabLabel = tab?.tab_label ?? "";
    const menuHint = resolveMessageMenuHint(message);
    const menuHintKey = menuHint
      ? `${menuHint.preset}:${[...menuHint.exclude_ids].sort().join("|")}`
      : "";
    const cacheKey = `${appDomain}:${appFocus}:${contentType}:${messageType}:${tabId}:${tabLabel}:${menuHintKey}`;

    const cached = messageMenuCache[cacheKey];
    if (cached !== undefined) {
      setContextMenuItems(cached);
      openMenu();
      return;
    }

    setContextMenuLoading(true);
    getMessageMenu(contentType, messageType, {
      ...(tab ? { tab_id: tab.tab_id, tab_label: tab.tab_label } : {}),
      ...(menuHint
        ? {
            menu_preset: menuHint.preset,
            menu_exclude_ids: [...menuHint.exclude_ids],
          }
        : {}),
    })
      .then((items) => {
        messageMenuCache[cacheKey] = items;
        setContextMenuItems(items);
        openMenu();
      })
      .catch(() => {
        setContextMenuItems([]);
        openMenu();
      })
      .finally(() => setContextMenuLoading(false));
    },
    [
      activeTabInfo,
      appDomain,
      appFocus,
      contextMenuItems,
      contextMenuLoading,
      message,
    ],
  );

  const showDate =
    message.date &&
    (!previousMessage ||
      !previousMessage.date ||
      !isSameDate(
        previousMessage.date.split("T")[0],
        message.date.split("T")[0],
      ));

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!isOnline || isBrowserOffline()) {
      event.preventDefault();
      notifyOfflineActionBlocked();
      return;
    }
    if (!isBrowserLocked) {
      event.preventDefault();
      const { clientX, clientY } = event;
      fetchContextMenuItems({ top: clientY, left: clientX });
    }
  };

  const menuIconColor =
    contextMenuLoading || menuPosition
      ? theme.palette.primary.main
      : theme.palette.mode === "dark"
        ? "#C8CBDE"
        : "#9EA6B8";

  const renderMenuIconButton = (sx: Record<string, unknown> = {}) => (
    <Box
      component={motion.div}
      onClick={handleContextMenu}
      whileTap={contextMenuLoading ? undefined : { scale: 0.85 }}
      animate={contextMenuLoading ? undefined : menuIconShakeAnimation}
      transition={menuIconShakeTransition}
      sx={{
        position: "static",
        zIndex: 12,
        pointerEvents: contextMenuLoading ? "none" : "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 25,
        height: 25,
        ...sx,
      }}
    >
      {contextMenuLoading ? (
        <CircularProgress size={20} sx={{ color: menuIconColor }} />
      ) : (
        <MenuEmojiIcon width={25} height={25} color={menuIconColor} />
      )}
    </Box>
  );

  const handleClose = async () => {
    setMenuPosition(null);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSelectedTextState("");
  };

  const handleReactionClick = async (reactionKey: string) => {
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    trackInteraction("reaction", 0);
    await sendReaction(reactionKey, message.message_id);
    if (message.type === "tabbed_message") {
      const lessonTitle = getLessonTitleFromAny(message);
      if (lessonTitle) {
        cacheLessonTitle(
          message.message_id,
          message.domain,
          message.focus,
          lessonTitle,
        );
      }
    }
    if (message.type === "tabbed_message" && message.tabbed_content?.length) {
      cacheTabbedMessageLabels(
        message.message_id,
        message.tabbed_content.map((t) => t.tab_label),
      );
    }
    setCurrentReaction(reactionKey);
    handleClose();
  };

  const handleSendMenuItem = async (
    text: string,
    description: string,
    chosen_text?: string,
  ) => {
    const sourceMessageId =
      message.type === "visualization_message"
        ? message.visualization?.source_message_id?.trim()
        : undefined;
    const targetMessageId = sourceMessageId || message.message_id;
    const tab_id =
      message.type === "tabbed_message" ? activeTabInfo?.tabId : undefined;
    window.dispatchEvent(new CustomEvent("messageMenuActionStarted"));
    sendMessageMenuPress(
      text,
      description,
      targetMessageId,
      chosen_text,
      tab_id,
    );
    handleClose();
  };

  const handleCopyLink = () => {
    const baseUrl = `${window.location.origin}/chat`;
    const url = new URL(baseUrl);

    url.searchParams.set("message_id", message.message_id);
    if (message.domain) {
      url.searchParams.set("domain", message.domain);
    }
    if (message.focus) {
      url.searchParams.set("focus", message.focus);
    }

    navigator.clipboard
      .writeText(url.toString())
      .then(() => {
        setCopyMessage("Content copied");
      })
      .catch((error) => console.error("Failed to copy link:", error));

    handleClose();
  };

  const handleAddToFavorite = async () => {
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    trackInteraction("favorite", 0);
    try {
      if (message.type === "tabbed_message") {
        const lessonTitle = getLessonTitleFromAny(message);
        if (lessonTitle) {
          cacheLessonTitle(
            message.message_id,
            message.domain,
            message.focus,
            lessonTitle,
          );
        }
      }
      const tabLabels =
        message.type === "tabbed_message" && !isSaved
          ? (message.tabbed_content?.map((t) => t.tab_label) ?? [])
          : undefined;
      await updateFavoriteStatus(message.message_id, !isSaved, tabLabels);
      setIsSaved(!isSaved);
    } catch (error) {
      console.error("Error updating favorite status:", error);
    } finally {
      handleClose();
    }
  };

  const handleAddToTag = async (tag: string) => {
    if (tags && tags.length >= 3) {
      setCopyMessage("You cannot add more than 3 tags");
      return;
    }
    try {
      if (message.type === "tabbed_message") {
        const lessonTitle = getLessonTitleFromAny(message);
        if (lessonTitle) {
          cacheLessonTitle(
            message.message_id,
            message.domain,
            message.focus,
            lessonTitle,
          );
        }
      }
      if (message.type === "tabbed_message" && message.tabbed_content?.length) {
        cacheTabbedMessageLabels(
          message.message_id,
          message.tabbed_content.map((t) => t.tab_label),
        );
      }
      const newTags = tags ? [...tags, tag] : [tag];
      setTags(newTags);
      await updateTagStatus(message.message_id, newTags);
    } catch (error) {
      console.error("Error updating tag status:", error);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    try {
      const newTags = tags ? tags.filter((item) => item !== tag) : [];
      setTags(newTags);
      await updateTagStatus(message.message_id, newTags);
    } catch (error) {
      console.error("Error removing tag:", error);
    }
  };

  const handleCopyMessageText = () => {
    let textToCopy = "";

    if (selectedTextState) {
      textToCopy = selectedTextState;
    } else {
      const selectedText = window.getSelection()?.toString().trim();
      if (selectedText) {
        textToCopy = selectedText;
      } else if (message.type === "tabbed_message") {
        textToCopy = tabbedMessageText || "";
      } else if (message.type === "visualization_message") {
        textToCopy = visualizationMenuText || defaultMenuSourceText;
      } else {
        textToCopy = defaultMenuSourceText;
      }
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopyMessage("Content copied");
      })
      .catch((error) => console.error("Failed to copy text:", error));
    handleClose();
  };

  const handleSendFeedback = () => {
    setMessages((prev) => {
      let feedbackMessageId = "";
      for (let i = prev.length - 1; i >= 0; i--) {
        if (message.type === "input" && message.message_type === "feedback") {
          feedbackMessageId = message.message_id;
          break;
        }
      }

      return prev.filter((message) => message.message_id !== feedbackMessageId);
    });

    toast.dismiss();
    toast.success("Thank you for your feedback!", {
      position: "top-right" as const,
      autoClose: 3000,
      closeOnClick: false,
      pauseOnHover: true,
    });
  };

  const showAvatar =
    !message.linked_with_previous || !nextMessage?.linked_with_previous;
  const isTabbedMessage = message.type === "tabbed_message";
  const isUserAudioMessage = !isBot && message.type === "audio";
  const userAudioBlockWidth = {
    xs: "min(100%, 300px)",
    md: "min(100%, 360px)",
  };

  if (message.isPlaceholder) {
    // No extra UI: streaming/tabbed content shows its own loading state. Placeholder
    // only reserves a list slot until the first chunk replaces this message.
    return null;
  }

  if (message.type === "text") {
    const hasTextBody = !!(message.text?.trim() || message.title?.trim());
    const hasReplyContext = !!(
      message.reply?.trim() ||
      message.reply_message_text?.trim() ||
      message.reply_message_id?.trim() ||
      message.reply_message_tab_id?.trim()
    );
    if (!hasTextBody && !hasReplyContext) {
      return null;
    }
  }
  if (message.type === "image" && !message.file_url) {
    return null;
  }
  if (
    message.type === "visualization_message" &&
    !hasRenderableVisualization(message)
  ) {
    return null;
  }

  const showFooterControls =
    ((message.message_type !== "system" &&
      message.type !== "calendar-week" &&
      message.type !== "input" &&
      !(isBot && message.type === "image")) ||
      isVisualizeOutputMessage) &&
    !isUserAudioMessage &&
    !isStatic &&
    !hideReactionsButton;

  const standardMessageMaxWidthSx = {
    xs: "100%",
    sm: "min(100%, 720px)",
    lg: "min(100%, 898px)",
  };

  const messageFragment = (
    <>
      {message.type === "flash_card" &&
        message.flash_card &&
        message.flash_card?.length > 0 && (
          <FlashCardMessage
            message={message}
            isBot={message.is_bot}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            variant={message.message_type}
            nextMessage={nextMessage}
            setLargeHigth={setLargeHigth}
            setActiveFlashCardText={setFlashCardText}
          />
        )}

      {message.type === "flash_card_image_collection" &&
        message.image_collection &&
        message.image_collection.length > 0 && (
          <FlashImageCollection
            message={message}
            isBot={isBot}
            variant={message.message_type}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
          />
        )}

      {message.type === "flipping_card" &&
        message.flipping_card &&
        message.flipping_card?.length > 0 && (
          <FlippingCardMessage
            message={message}
            isBot={message.is_bot}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            variant={message.message_type}
            setActiveFlippingCardText={setFlippingCardText}
          />
        )}

      {message.type === "tabbed_message" &&
        message.tabbed_content &&
        message.tabbed_content.length > 0 && (
          <TabbedMessage
            message={message}
            isBot={message.is_bot}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            variant={message.message_type}
            onActiveTabContentChange={setTabbedMessageText}
            onActiveTabChange={setActiveTabInfo}
            onActiveFlashCardTextChange={setTabbedFlashCardText}
            onActiveFlippingCardTextChange={setTabbedFlippingCardText}
            nextMessage={nextMessage}
            setLargeHigth={setLargeHigth}
          />
        )}

      {message.type === "text" &&
        !message.file_url &&
        (message?.text ||
          message?.title ||
          message.reply?.trim() ||
          message.reply_message_text?.trim() ||
          message.reply_message_id?.trim() ||
          message.reply_message_tab_id?.trim()) && (
          <TextMessage
            message={message}
            isBot={message.is_bot}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            variant={message.message_type}
            nextMessage={nextMessage}
            setLargeHigth={setLargeHigth}
          />
        )}

      {message.type === "audio" &&
        message.file_url &&
        (isUserAudioMessage ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              // Match AudioPlayer card width (narrow box); keep footer under the same right edge as the bubble.
              alignItems: "flex-end",
            }}
          >
            <AudioPlayer
              text={message.text}
              messageId={message.message_id}
              audioUrl={message.file_url}
              isBot={isBot}
              currentReaction={currentReaction}
              isSaved={isSaved}
              markedIds={markedIds}
              isTags={tags}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              handleRemoveTag={handleRemoveTag}
              transcription={resolvedTranscript.transcription}
              transcriptionTimed={resolvedTranscript.transcriptionTimed}
            />
            {message.message_type !== "system" &&
              !isStatic &&
              !hideReactionsButton && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    maxWidth: { xs: 220, sm: 250 },
                    mt: 1.25,
                    mb: 0.75,
                    py: { xs: 0.75, sm: 1 },
                    boxSizing: "border-box",
                  }}
                >
                  {renderMenuIconButton()}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Box
                      onClick={() =>
                        scrollToPreviousUserMessage(message.message_id)
                      }
                      sx={{
                        cursor: "pointer",
                        mr: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronUpIcon
                        color={theme.palette.header.userBlock.linkColor}
                        strokeWidth={4}
                      />
                    </Box>
                    <AvatarWithCircle
                      img={userAvatar}
                      size="36px"
                      userId={message.user_id}
                      hideText={true}
                      sx={{ my: 0 }}
                    />
                    <Box
                      onClick={() =>
                        scrollToNextUserMessage(message.message_id)
                      }
                      sx={{
                        cursor: "pointer",
                        ml: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronDownIcon
                        color={theme.palette.header.userBlock.linkColor}
                        strokeWidth={4}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
        ) : (
          <AudioPlayer
            text={message.text}
            messageId={message.message_id}
            audioUrl={message.file_url}
            isBot={isBot}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            transcription={resolvedTranscript.transcription}
            transcriptionTimed={resolvedTranscript.transcriptionTimed}
          />
        ))}

      {message.type === "video" && message.file_url && (
        <VideoPlayer
          videoUrl={message.file_url}
          title={message.title}
          messageId={message.message_id}
          isBot={isBot}
          currentReaction={currentReaction}
          isSaved={isSaved}
          markedIds={markedIds}
          isTags={tags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
        />
      )}

      {message.type === "image_collection" &&
        message.image_collection &&
        message.image_collection.length > 0 && (
          <ImageCollection
            message={message}
            isBot={isBot}
            variant={message.message_type}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
            nextMessage={nextMessage}
            setLargeHigth={setLargeHigth}
          />
        )}

      {message.type === "image" && message.file_url && (
        <ImageMessage
          src={message.file_url}
          alt={message.text}
          isSystem={message.message_type === "system"}
          currentReaction={currentReaction}
          isSaved={isSaved}
          markedIds={markedIds}
          isTags={tags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
        />
      )}

      {message.type === "visualization_message" &&
        hasRenderableVisualization(message) && (
          <VisualizationMessageContent
            message={message}
            currentReaction={currentReaction}
            isSaved={isSaved}
            markedIds={markedIds}
            isTags={tags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            handleRemoveTag={handleRemoveTag}
          />
        )}

      {message.type === "file" && message.file_url && (
        <AuthenticatedFileLink
          href={message.file_url}
          sx={{ textDecoration: "underline", color: "blue" }}
        >
          {message.text || "Download file"}
        </AuthenticatedFileLink>
      )}

      {message.type === "charts" && <MessageCharts message={message} />}

      {message.type === "button" && message.buttons && (
        <MessageButtons
          message={message}
          buttons={message.buttons}
          state={message.state}
          messageId={message.message_id}
          userId={message.user_id}
          currentReaction={currentReaction}
          isSaved={isSaved}
          markedIds={markedIds}
          isTags={tags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
        />
      )}

      {message.type === "calendar-week" && message.days && (
        <CalendarWeek
          days={message.days}
          messageId={message.message_id}
          availableDays={message.availableDays}
          availableHoursPerDay={message.availableHoursPerDay}
        />
      )}

      {message.type === "input" &&
        message.is_text_input &&
        message.stars_count && (
          <FeedbackInput
            title={message.title}
            text={message.text}
            isTextInput={message.is_text_input}
            starsCount={message.stars_count}
            initialSelectedStars={message.selected_stars}
            onSubmit={handleSendFeedback}
          />
        )}
      {message.type === "crash_course_form" && (
        <CrashCourseForm
          messageId={message.message_id}
          onFormSubmit={async (formData) => {
            try {
              await submitCrashCourseForm(formData);
            } catch (error) {
              console.error("Error submitting crash course form:", error);
            }
          }}
        />
      )}
    </>
  );

  const footerActionControls = (
    <>
      {renderMenuIconButton()}

      {isBot && ttsAllowedForMessageType && message.type === "text" && (
        <Box component={motion.div} whileTap={{ scale: 0.85 }}>
          <IndependentTtsButton
            message={message}
            disabled={!(message.text || message.title || message.reply)}
          />
        </Box>
      )}

      {isBot &&
        ttsAllowedForMessageType &&
        message.type === "tabbed_message" && (
          <Box component={motion.div} whileTap={{ scale: 0.85 }}>
            <IndependentTtsButton
              message={message}
              tabId={activeTabInfo?.tabId}
              speakableTextForVersion={tabbedTtsSpeakableText}
              disabled={
                ttsDisabledForCurrentTabbedTab ||
                !activeTabInfo?.tabId ||
                !tabbedTtsSpeakableText.trim()
              }
            />
          </Box>
        )}
    </>
  );

  return (
    <motion.div
      key={messageTheme}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        ref={(el: HTMLDivElement | null) => {
          messageRef.current = el;
          if (setMessageRef) {
            setMessageRef(el);
          }
        }}
        sx={{
          position: "relative",
          mb: 2,
        }}
        data-user-message={!isBot ? "true" : "false"}
        data-message-id={message.message_id}
        onContextMenu={isMobile ? undefined : handleContextMenu}
      >
        {showDate && !isStatic && <DateBadge date={message.date} />}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isUserAudioMessage
              ? isBot
                ? "flex-start"
                : "flex-end"
              : !isBot
                ? "flex-end"
                : "stretch",
            mt: 2,
            width: "100%",
          }}
        >
          {isUserAudioMessage ? (
            <Box
              sx={{
                position: "relative",
                borderRadius: 2,
                p: 0,
                minWidth: 0,
                maxWidth: userAudioBlockWidth,
                width: userAudioBlockWidth,
                ml: "auto",
                ...(!isBot
                  ? { borderTopRightRadius: 0 }
                  : { borderTopLeftRadius: 0 }),
              }}
            >
              {messageFragment}
            </Box>
          ) : (
            <Box
              sx={{
                // User text (non-tabbed): same idea as user audio — narrow column (max-content) + ml:auto
                // so the block sits on the right; footer width:100% matches bubble; space-between = emoji left, avatar right.
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                alignSelf: isBot ? "flex-start" : "flex-end",
                ...(isTabbedMessage
                  ? {
                      width: "100%",
                      maxWidth: "100%",
                    }
                  : {
                      width: "max-content",
                      maxWidth: standardMessageMaxWidthSx,
                      ...(!isBot ? { ml: "auto" } : {}),
                    }),
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  p: 0,
                  minWidth: 0,
                  ...(isTabbedMessage
                    ? {
                        maxWidth: "100%",
                        width: "100%",
                        ml: 0,
                      }
                    : {
                        maxWidth: standardMessageMaxWidthSx,
                        // User: shrink-wrap so the bubble and icon row share one right-aligned column.
                        width: isBot ? "auto" : "fit-content",
                        ml: 0,
                      }),
                  ...(!isBot
                    ? { borderTopRightRadius: 0 }
                    : { borderTopLeftRadius: 0 }),
                }}
              >
                {messageFragment}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  width: "100%",
                  mt: 2,
                  mb: { xs: 0.5, sm: 0.75, md: 2 },
                  justifyContent: isBot
                    ? "space-between"
                    : showFooterControls
                      ? "space-between"
                      : "flex-end",
                  gap: isBot ? 1 : 1.5,
                  pl: isBot ? 1.5 : 0,
                  pr: isBot ? 0 : 1.5,
                  boxSizing: "border-box",
                }}
              >
                {isBot ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        mb: 0,
                        pl: 0,
                        alignItems: "center",
                        gap: "5px",
                        mt: 0,
                        width:
                          message.type === "image"
                            ? { xs: 300, md: 400, lg: 500, xl: 500 }
                            : "auto",
                        maxWidth: message.type === "image" ? "100%" : undefined,
                        flex: "0 0 auto",
                        justifyContent:
                          message.type === "image"
                            ? "space-between"
                            : "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {showAvatar && (
                          <Box
                            onClick={() =>
                              scrollToPreviousLessonMessage(message.message_id)
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <ChevronUpIcon
                              color={theme.palette.header.userBlock.linkColor}
                              strokeWidth={4}
                            />
                          </Box>
                        )}
                        <Box
                          component={showAvatar ? "img" : "div"}
                          src={
                            showAvatar
                              ? botAvatar
                                ? images[botAvatar]
                                : images.botImage
                              : undefined
                          }
                          alt="Bot avatar"
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            maxWidth: "none",
                            objectFit: "cover",
                          }}
                        />
                        {showAvatar && (
                          <Box
                            onClick={() =>
                              scrollToNextLessonMessage(message.message_id)
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <ChevronDownIcon
                              color={theme.palette.header.userBlock.linkColor}
                              strokeWidth={4}
                            />
                          </Box>
                        )}
                      </Box>
                      {message.type === "image" &&
                        message.message_type !== "system" &&
                        !isStatic &&
                        !hideReactionsButton &&
                          renderMenuIconButton({ transform: "translateX(-8px)" })}
                    </Box>
                    {isTabbedMessage && (
                      <TabbedMessageStarRating
                        message={message}
                        activeTabId={activeTabInfo?.tabId}
                        activeTabLabel={activeTabInfo?.tabLabel}
                        activeTabText={tabbedMessageText}
                      />
                    )}
                    {showFooterControls && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "14px",
                        }}
                      >
                        {footerActionControls}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    {showFooterControls && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "14px",
                        }}
                      >
                        {footerActionControls}
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        mb: 0,
                        py: 0,
                        alignItems: "center",
                        gap: "8px",
                        mt: 0,
                        width: "auto",
                        maxWidth: "none",
                        flex: "0 0 auto",
                        ml: 0,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Box
                          onClick={() =>
                            scrollToPreviousUserMessage(message.message_id)
                          }
                          sx={{
                            cursor: "pointer",
                            mr: 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <ChevronUpIcon
                            color={theme.palette.header.userBlock.linkColor}
                            strokeWidth={4}
                          />
                        </Box>
                        <AvatarWithCircle
                          img={userAvatar}
                          size="36px"
                          userId={message.user_id}
                          hideText={true}
                          sx={{ my: 0 }}
                        />
                        <Box
                          onClick={() =>
                            scrollToNextUserMessage(message.message_id)
                          }
                          sx={{
                            cursor: "pointer",
                            ml: 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <ChevronDownIcon
                            color={theme.palette.header.userBlock.linkColor}
                            strokeWidth={4}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {((message.message_type !== "system" &&
          message.type !== "calendar-week" &&
          message.type !== "input") ||
          isVisualizeOutputMessage) &&
          !hideReactionsButton && (
            <Box ref={menuRef}>
              <MessageMenuDropdown
                message={message}
                handleCopyMessageText={handleCopyMessageText}
                menuPosition={menuPosition}
                handleClose={handleClose}
                handleReactionClick={handleReactionClick}
                handleCopyLink={handleCopyLink}
                menuItems={contextMenuItems ?? []}
                menuItemsLoading={contextMenuLoading}
                handleSendMenuItem={handleSendMenuItem}
                handleAddToFavorite={handleAddToFavorite}
                isSaved={isSaved}
                text={
                  message.type === "tabbed_message"
                    ? tabbedMessageText
                    : message.type === "visualization_message"
                      ? visualizationMenuText || defaultMenuSourceText
                      : defaultMenuSourceText
                }
                hasUserSelectedText={!!selectedTextState}
                selectedText={
                  message.flash_card
                    ? flashCardText
                    : message.flipping_card
                      ? flippingCardText
                      : message.type === "tabbed_message"
                        ? tabbedFlashCardText ||
                          tabbedFlippingCardText ||
                          selectedTextState
                        : selectedTextState
                }
                handleAddToTag={handleAddToTag}
                tags={tags}
                handleRenameTag={async (newTags: string[]) => {
                  setTags(newTags);
                  await updateTagStatus(message.message_id, newTags);
                }}
                onFeedbackSubmitted={handleFeedbackSubmitted}
              />
            </Box>
          )}
      </Box>
    </motion.div>
  );
};

export default MessageBubble;
